import type { SxProps, Theme } from '@mui/material/styles';

export const resourcesStyles: Record<string, SxProps<Theme>> = {
  // Full-bleed mint band — background spans the entire viewport width.
  root: (theme) => ({
    backgroundColor: theme.palette.brand.mint,
    color: theme.palette.text.primary,
    paddingInline: { xs: theme.spacing(3), md: 'clamp(32px, 5vw, 80px)' },
    paddingTop: { xs: theme.spacing(4), md: 'clamp(32px, 4vw, 56px)' },
    paddingBottom: { xs: theme.spacing(6), md: 'clamp(64px, 8vw, 120px)' },
    width: '100%',
  }),
  // Inner grid is capped and centered so the band stays full-width while
  // the content aligns with the rest of the page.
  content: (theme) => ({
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(280px, 1fr) minmax(0, 1.15fr)' },
    gap: { xs: theme.spacing(4), md: theme.spacing(6) },
    alignItems: 'start',
    width: '100%',
    maxWidth: 1560,
    marginInline: 'auto',
  }),
  intro: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
  },
  heading: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    // Figma: Manrope Medium 44 / 56.
    fontSize: { xs: '1.75rem', md: '2rem', lg: '2.75rem' },
    lineHeight: { xs: 1.2, lg: '3.5rem' },
    color: theme.palette.common.black,
    margin: 0,
  }),
  headingAccent: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: theme.palette.primary.main,
  }),
  body: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    // Figma: Manrope Medium 20 / 30.
    fontSize: { xs: '1rem', lg: '1.25rem' },
    lineHeight: { xs: 1.5, lg: '1.875rem' },
    color: theme.palette.common.black,
  }),
  // Figma: a distinct blue (#0611F6) from primary.main (#0410FF).
  link: (theme) => ({
    ...theme.typography.linkStrong,
    color: theme.palette.surface.linkBlue,
    width: 'fit-content',
    transition: theme.transitions.create('color'),
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  }),
  // A grid (not two independent per-card flex columns), with each card's
  // top/bottom explicitly placed by index in Resources.tsx — both white
  // "top" sections share one grid row, and both blue "bottom" sections
  // share the next, so each pair sizes to its tallest member instead of
  // drifting apart whenever the two cards' body copy wraps to a different
  // number of lines at a given width.
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
    gap: 3,
  },
  // Dissolves into the grid so its two children (cardTop/cardBottom) become
  // direct grid items that Resources.tsx can place explicitly.
  card: {
    display: 'contents',
  },
  cardTop: (theme) => ({
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(3),
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    overflow: 'hidden',
  }),
  cardBottom: (theme) => ({
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(3),
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    overflow: 'hidden',
  }),
  cardTitle: (theme) => ({
    fontFamily: theme.typography.displayItalic.fontFamily,
    fontStyle: 'italic',
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: { xs: '1.25rem', md: '1.5rem' },
    lineHeight: 1.2,
    color: theme.palette.primary.main,
    margin: 0,
  }),
  // Figma: color #00243E specifically — this card's subtext uses the
  // (older) navy.deep shade, not the newer text.primary (#04233C) used
  // elsewhere like the Overview Panel.
  cardBody: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '1rem',
    lineHeight: 1.4,
    color: theme.palette.brand.navy.deep,
    margin: 0,
  }),
  cardEyebrow: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: 800,
    fontSize: '0.875rem',
    lineHeight: 1.35,
    letterSpacing: '-0.154px',
    color: theme.palette.common.white,
    margin: 0,
  }),
  cardFooter: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: '0.875rem',
    lineHeight: 1.35,
    color: theme.palette.common.white,
    margin: 0,
  }),
};
